import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import {
  MoreHoriz,
  GitHub,
  OpenInNew,
  Refresh as RestartIcon,
  Build as RebuildIcon,
  Visibility as ViewIcon,
  ContentCopy,
  Check as CheckIcon,
  Schedule,
  Edit as RenameIcon,
  Delete as DeleteIcon,
  Language as DomainIcon,
  Storage as PostgresIcon,
  Inbox as SqsIcon,
  ViewList as DdbIcon,
  Folder as S3Icon,
  Psychology as LlmIcon,
  VpnKey as SecretIcon,
  Extension as GenericServiceIcon,
} from "@mui/icons-material";
import StatusOrb from "./StatusOrb";
import QuickMetrics from "./QuickMetrics";

const cardVariants = {
  idle: { scale: 1, y: 0 },
  hover: {
    scale: 1.01,
    y: -2,
    boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
  },
  tap: { scale: 0.99 },
};

const overlayVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

const statusBorderColors = {
  building: "#f59e0b",
  deploying: "#F97316",
  live: "#22c55e",
  error: "#ef4444",
  idle: "transparent",
};

// Daari service kind → icon + brand tint. Matches ServiceInstanceCard so
// the same shape reads consistently on both sides of the graph.
const SERVICE_KIND_META = {
  postgres: { label: "pg", Icon: PostgresIcon, tint: "#38bdf8" },
  awssqs: { label: "sqs", Icon: SqsIcon, tint: "#f472b6" },
  awssimplequeue: { label: "sqs", Icon: SqsIcon, tint: "#f472b6" },
  awsdynamodb: { label: "ddb", Icon: DdbIcon, tint: "#a78bfa" },
  awss3bucket: { label: "s3", Icon: S3Icon, tint: "#fbbf24" },
  "llm-gateway": { label: "llm", Icon: LlmIcon, tint: "#34d399" },
  llm_gateway: { label: "llm", Icon: LlmIcon, tint: "#34d399" },
  DaariSecret: { label: "secret", Icon: SecretIcon, tint: "#94a3b8" },
};
const serviceKindMeta = (kind) =>
  SERVICE_KIND_META[kind] || { label: kind || "svc", Icon: GenericServiceIcon, tint: "#94a3b8" };

/**
 * Format relative time
 */
const formatRelativeTime = (date) => {
  if (!date) return "";
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
};

/**
 * Truncate URL for display
 */
const truncateUrl = (url, maxLength = 30) => {
  if (!url) return "";
  // Remove protocol
  const cleaned = url.replace(/^https?:\/\//, "");
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength) + "...";
};

/**
 * Build status section
 */
const BuildSection = ({ build, isDarkMode }) => {
  const statusChipProps = {
    pending: { label: "Preparing", color: "#6366f1", bg: "rgba(99, 102, 241, 0.12)" },
    building: { label: "Building", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.12)" },
    ready: { label: "Ready", color: "#22c55e", bg: "rgba(34, 197, 94, 0.12)" },
    failed: { label: "Failed", color: "#ef4444", bg: "rgba(239, 68, 68, 0.12)" },
  };

  const props = statusChipProps[build.status] || statusChipProps.pending;

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: isDarkMode ? "#64748b" : "#94a3b8",
          fontWeight: 500,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          letterSpacing: "0.05em",
        }}
      >
        Build
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <Chip
          size="small"
          label={props.label}
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            background: props.bg,
            color: props.color,
            border: "none",
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: isDarkMode ? "#94a3b8" : "#64748b",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.5,
        }}
      >
        <Schedule sx={{ fontSize: "0.75rem" }} />
        #{build.buildCounter || 0} {build.createdOn ? `• ${formatRelativeTime(build.createdOn)}` : ""}
      </Typography>
    </Box>
  );
};

/**
 * Runtime status section
 */
const RuntimeSection = ({ runtime, isDarkMode }) => {
  const statusChipProps = {
    running: { label: "Running", color: "#22c55e", bg: "rgba(34, 197, 94, 0.12)" },
    deploying: { label: "Deploying", color: "#F97316", bg: "rgba(234, 88, 12, 0.12)" },
    error: { label: "Error", color: "#ef4444", bg: "rgba(239, 68, 68, 0.12)" },
    stopped: { label: "Stopped", color: "#64748b", bg: "rgba(100, 116, 139, 0.12)" },
  };

  const props = statusChipProps[runtime.status] || statusChipProps.stopped;

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: isDarkMode ? "#64748b" : "#94a3b8",
          fontWeight: 500,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          letterSpacing: "0.05em",
        }}
      >
        Runtime
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <Chip
          size="small"
          label={props.label}
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            background: props.bg,
            color: props.color,
            border: "none",
          }}
        />
      </Box>
      <Box sx={{ mt: 0.5 }}>
        <QuickMetrics runtime={runtime} compact />
      </Box>
    </Box>
  );
};

/**
 * Quick action button
 */
const QuickActionButton = ({ icon: Icon, label, onClick, disabled }) => (
  <Tooltip title={label} arrow>
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
      sx={{
        background: "rgba(255,255,255,0.9)",
        backdropFilter: "blur(4px)",
        "&:hover": {
          background: "#fff",
          transform: "scale(1.1)",
        },
        transition: "all 0.2s ease",
      }}
    >
      <Icon sx={{ fontSize: "1rem" }} />
    </IconButton>
  </Tooltip>
);

/**
 * Main unified app card component
 */
export function UnifiedAppCard({
  app,
  onOpenPipeline,
  onRestart,
  onRebuild,
  onRename,
  onDelete,
  onManageDomains,
  onOpenUrl,
  onClick,
  boundServices = [],
  onOpenBoundService,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [isHovered, setIsHovered] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const borderColor = statusBorderColors[app.overallStatus] || "transparent";
  const showPulse = app.overallStatus === "building" || app.overallStatus === "deploying";

  const handleCopyUrl = (e) => {
    e.stopPropagation();
    if (app.runtime?.url) {
      navigator.clipboard.writeText(app.runtime.url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const handleOpenUrl = (e) => {
    e.stopPropagation();
    if (app.runtime?.url) {
      window.open(app.runtime.url, "_blank");
    }
  };

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    e?.stopPropagation?.();
    setMenuAnchor(null);
  };

  const handleMenuAction = (action) => (e) => {
    e?.stopPropagation?.();
    handleMenuClose();
    action?.(app);
  };

  return (
    <Card
      component={motion.div}
      variants={cardVariants}
      initial="idle"
      whileHover="hover"
      whileTap="tap"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onClick?.(app)}
      sx={{
        borderRadius: "16px",
        // Glass-morphism — matches showcase/wizard cards.
        background: isDarkMode
          ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
          : "rgba(255,255,255,0.85)",
        border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"}`,
        backdropFilter: "blur(20px)",
        boxShadow: isDarkMode
          ? "0 4px 14px rgba(0,0,0,0.35)"
          : "0 1px 3px rgba(15,23,42,0.06)",
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
        transition:
          "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: isDarkMode
            ? "rgba(253,186,116,0.4)"
            : "rgba(234,88,12,0.25)",
          boxShadow: isDarkMode
            ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(234,88,12,0.3), 0 0 32px rgba(234,88,12,0.15)"
            : "0 8px 24px rgba(15,23,42,0.10), 0 0 0 1px rgba(234,88,12,0.18)",
        },
      }}
    >
      {/* Animated border for active states */}
      {showPulse && (
        <Box
          component={motion.div}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "16px",
            border: `2px solid ${borderColor}`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <StatusOrb status={app.overallStatus} size="medium" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? "#f1f5f9" : "#1e293b",
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {app.displayName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "#64748b" : "#94a3b8",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {(app.name || "").replace(/-image$/, "")}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ color: isDarkMode ? "#64748b" : "#94a3b8" }}
        >
          <MoreHoriz />
        </IconButton>

        {/* Three-dots Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{
            sx: {
              minWidth: 180,
              borderRadius: "12px",
              background: isDarkMode ? "#1e293b" : "#ffffff",
              border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
              boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={handleMenuAction(onOpenPipeline)}>
            <ListItemIcon>
              <ViewIcon sx={{ fontSize: "1.2rem", color: "#F97316" }} />
            </ListItemIcon>
            <ListItemText
              primary="View Pipeline"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
          <MenuItem onClick={handleMenuAction(onRebuild)}>
            <ListItemIcon>
              <RebuildIcon sx={{ fontSize: "1.2rem", color: "#f59e0b" }} />
            </ListItemIcon>
            <ListItemText
              primary="Rebuild"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
          <MenuItem
            onClick={handleMenuAction(onRestart)}
            disabled={app.runtime?.pods === 0}
          >
            <ListItemIcon>
              <RestartIcon sx={{ fontSize: "1.2rem", color: "#22c55e" }} />
            </ListItemIcon>
            <ListItemText
              primary="Restart"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
          <MenuItem onClick={handleMenuAction(onManageDomains)}>
            <ListItemIcon>
              <DomainIcon sx={{ fontSize: "1.2rem", color: "#F97316" }} />
            </ListItemIcon>
            <ListItemText
              primary="Domains"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleMenuAction(onRename)}>
            <ListItemIcon>
              <RenameIcon sx={{ fontSize: "1.2rem", color: "#8b5cf6" }} />
            </ListItemIcon>
            <ListItemText
              primary="Rename"
              primaryTypographyProps={{ fontSize: "0.875rem" }}
            />
          </MenuItem>
          <MenuItem onClick={handleMenuAction(onDelete)}>
            <ListItemIcon>
              <DeleteIcon sx={{ fontSize: "1.2rem", color: "#ef4444" }} />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              primaryTypographyProps={{ fontSize: "0.875rem", color: "#ef4444" }}
            />
          </MenuItem>
        </Menu>
      </Box>

      {/* Status Sections */}
      <Box
        sx={{
          px: 2,
          pb: 2,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        <BuildSection build={app.build} isDarkMode={isDarkMode} />
        <RuntimeSection runtime={app.runtime} isDarkMode={isDarkMode} />
      </Box>

      {/* Bound services chip strip — renders nothing when empty so the
          card height stays constant for standalone apps. Chips deep-link
          to the target Daari instance via /daari?openInstance=... */}
      {Array.isArray(boundServices) && boundServices.length > 0 && (
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
            rowGap: 0.5,
          }}
        >
          {boundServices.slice(0, 4).map((svc, idx) => {
            const meta = serviceKindMeta(svc?.kind);
            const KindIcon = meta.Icon;
            const key = svc?.k8sName || `${svc?.kind}-${svc?.displayName}-${idx}`;
            return (
              <Tooltip
                key={key}
                title={
                  svc?.k8sName
                    ? `${meta.label}/${svc.displayName || svc.k8sName} — open on Daari`
                    : `${meta.label}/${svc?.displayName || "service"}`
                }
                arrow
              >
                <Chip
                  size="small"
                  icon={
                    <KindIcon
                      sx={{
                        fontSize: "0.85rem !important",
                        color: `${meta.tint} !important`,
                      }}
                    />
                  }
                  label={`${meta.label}/${svc?.displayName || svc?.k8sName || "?"}`}
                  onClick={
                    onOpenBoundService
                      ? (e) => {
                          e.stopPropagation();
                          onOpenBoundService(svc);
                        }
                      : undefined
                  }
                  clickable={Boolean(onOpenBoundService)}
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    "& .MuiChip-icon": { ml: 0.5, mr: -0.25 },
                    background: isDarkMode
                      ? "rgba(148,163,184,0.10)"
                      : "rgba(15,23,42,0.05)",
                    color: isDarkMode ? "#e2e8f0" : "#334155",
                    border: `1px solid ${meta.tint}44`,
                    cursor: onOpenBoundService ? "pointer" : "default",
                    "&:hover": onOpenBoundService
                      ? {
                          background: `${meta.tint}22`,
                          borderColor: `${meta.tint}88`,
                        }
                      : undefined,
                  }}
                />
              </Tooltip>
            );
          })}
          {boundServices.length > 4 && (
            <Chip
              size="small"
              label={`+${boundServices.length - 4}`}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                background: isDarkMode
                  ? "rgba(148,163,184,0.08)"
                  : "rgba(15,23,42,0.04)",
                color: isDarkMode ? "#94a3b8" : "#64748b",
                border: `1px dashed ${isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.10)"}`,
              }}
            />
          )}
        </Box>
      )}

      {/* Footer with Git & URL */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: isDarkMode ? "rgba(0,0,0,0.2)" : "#f8fafc",
          borderTop: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* Git URL */}
        {app.build?.gitUrl && (
          <Tooltip title={app.build.gitUrl} arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: isDarkMode ? "#64748b" : "#94a3b8",
                fontSize: "0.75rem",
                overflow: "hidden",
              }}
            >
              <GitHub sx={{ fontSize: "0.9rem" }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {truncateUrl(app.build.gitUrl, 20)}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* App URL */}
        {app.runtime?.url && (
          <Tooltip title={app.runtime.url} arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: isDarkMode ? "#64748b" : "#94a3b8",
                fontSize: "0.75rem",
                overflow: "hidden",
                cursor: "pointer",
                "&:hover": {
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                },
              }}
              onClick={handleOpenUrl}
            >
              <OpenInNew sx={{ fontSize: "0.9rem" }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "120px",
                }}
              >
                {truncateUrl(app.runtime.url, 18)}
              </Typography>
              <Tooltip title={copiedUrl ? "Copied!" : "Copy URL"} arrow>
                <IconButton
                  size="small"
                  onClick={handleCopyUrl}
                  sx={{
                    p: 0.25,
                    color: copiedUrl ? "#22c55e" : "inherit",
                  }}
                >
                  {copiedUrl ? (
                    <CheckIcon sx={{ fontSize: "0.75rem" }} />
                  ) : (
                    <ContentCopy sx={{ fontSize: "0.75rem" }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Hover Actions Overlay */}
      <AnimatePresence>
        {isHovered && (
          <Box
            component={motion.div}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.15 }}
            sx={{
              position: "absolute",
              // Sit just above the footer band (~50px). When the
              // bound-services chip row renders it inserts ~28px, so
              // shift the overlay up to keep QuickActionButtons clear.
              bottom:
                Array.isArray(boundServices) && boundServices.length > 0
                  ? 78
                  : 50,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 1,
              py: 1,
              background: `linear-gradient(transparent, ${isDarkMode ? "rgba(15,23,42,0.95)" : "rgba(255,255,255,0.95)"})`,
              zIndex: 2,
            }}
          >
            <QuickActionButton
              icon={ViewIcon}
              label="View Pipeline"
              onClick={() => onOpenPipeline?.(app)}
            />
            <QuickActionButton
              icon={RestartIcon}
              label="Restart"
              onClick={() => onRestart?.(app)}
              disabled={app.runtime?.pods === 0}
            />
            <QuickActionButton
              icon={RebuildIcon}
              label="Rebuild"
              onClick={() => onRebuild?.(app)}
            />
          </Box>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default UnifiedAppCard;
